<template>
  <section
    class="purchase-successful"
    :class="{
      'purchase-successful--ppv5': hasPPV5,
    }"
  >
    <BittsButton
      :center-icon="['fak', 'x']"
      variant="ghost"
      type="white"
      size="large"
      class="close-icon"
      data-testid="close-icon"
      @click="handleClose(true)"
    />
    <div class="welcome-to-the-team__content">
      <BittsSvg svg="logoNoText" class="welcome-to-the-team__logo" />
      <h1 class="main-title">
        {{ copyBase.purchaseSuccessful.title }}
      </h1>
      <BittsUserStack
        v-if="hasPPV5"
        data-testid="large-user-stack"
        :users
        size="large"
        class="mb-24"
      />
      <p class="description">
        {{
          hasOnboardingState
            ? copyBase.purchaseSuccessful.descriptionOnboarding
            : copyBase.purchaseSuccessful.description
        }}
      </p>
      <div v-if="!hasOnboardingState" class="button-grid">
        <component
          :is="hasPPV5 ? BittsButton : 'router-link'"
          v-for="link in routerLinks"
          :key="link.name"
          v-bind="getProps(link)"
          v-on="getEventHandlers(link)"
        >
          <FontAwesomeIcon
            class="mb-2 text-info-accent"
            :icon="['fak', link.icon]"
            :style="{ height: '16px', width: '16px' }"
          />
          {{ link.title }}
        </component>
      </div>
      <BittsCallout
        v-if="migratedUsers.length && !hasOnboardingState"
        data-testid="migration-callout"
        class="max-w-[670px] mt-16"
        size="small"
        type="info"
        :title="migrationCalloutTitle"
        subtitle="Your new Connector plan comes with Crossbeam for Sales so we auto-upgraded anyone who is an Admin or Partner Manager for you. You can change this on the Teams page."
      >
        <template #left-item>
          <FontAwesomeIcon
            :icon="['fak', 'artificial-intelligence']"
            class="text-24 mr-12 text-info-accent"
          />
        </template>
        <template #right-item>
          <BittsUserStack :users="migratedUsers" size="small" />
        </template>
      </BittsCallout>
      <BittsButton
        size="large"
        v-bind="continueButtonProps as BittsButtonProps"
        @click="handleClose"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsButtonProps,
  BittsCallout,
  BittsSvg,
  BittsUserStack,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';

import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { V4_BILLING_COPY, V5_BILLING_COPY } from '@/constants/billing';
import { TEMP_PPV5 } from '@/constants/feature_flags';
import { BASE_ROUTES } from '@/constants/routes';
import { PARTNER_MANAGER } from '@/constants/team';
import { useFeatureFlagStore, useTeamStore } from '@/stores';

useHead({ title: 'Welcome' });

const router = useRouter();
const teamStore = useTeamStore();
const ffStore = useFeatureFlagStore();

const hasPPV5 = computed(() => {
  return ffStore.hasFeatureFlag(TEMP_PPV5);
});

const copyBase = computed(() => {
  return hasPPV5.value ? V5_BILLING_COPY : V4_BILLING_COPY;
});

const { coreUsers, salesOnlyUsers } = storeToRefs(teamStore);
const users = computed(() =>
  [...coreUsers.value, ...salesOnlyUsers.value].map((auth) => auth.user),
);

const { currentOrg } = useAuth();
const isOnboarded = computed(() => currentOrg.value.setup_menu_hidden);
const hasOnboardingState = computed(() => {
  return !isOnboarded.value && hasPPV5.value;
});
const continueButtonProps = computed(() => {
  return hasOnboardingState.value
    ? {
        text: 'Finish onboarding',
        type: 'neutral',
        variant: 'solid',
        class: 'mt-16',
      }
    : {
        text: 'Continue to Crossbeam',
        type: hasPPV5.value ? 'white' : 'neutral',
        variant: hasPPV5.value ? 'ghost' : 'solid',
        class: hasPPV5.value ? 'mt-40' : 'mt-16',
      };
});

const routerLinks = computed(() => {
  return hasPPV5.value
    ? [
        {
          name: BASE_ROUTES.REPORTS,
          icon: 'mapping',
          dataTestid: 'report-action',
          title: 'Create an Advanced Report',
        },
        {
          name: BASE_ROUTES.DEAL_NAVIGATOR,
          icon: 'opportunity',
          dataTestid: 'deal-nav-action',
          title: 'Win faster with Deal Navigator',
        },
        {
          name: 'create_population',
          icon: 'population',
          dataTestid: 'population-action',
          title: 'Create a Custom Population',
        },
        {
          name: 'team',
          icon: 'team',
          dataTestid: 'team-action',
          title: 'Invite more teammates',
        },
      ]
    : [
        {
          name: 'new_population',
          icon: 'population',
          dataTestid: 'population-action',
          title: 'Create a Custom Population',
        },
        {
          name: 'collaborate',
          icon: 'report',
          dataTestid: 'shared-list-action',
          title: 'Create a Shared List',
        },
        {
          name: BASE_ROUTES.REPORTS,
          icon: 'mapping',
          dataTestid: 'report-action',
          title: 'Create an Advanced Report',
        },
        {
          name: 'integrations',
          icon: 'integration',
          dataTestid: 'integrations-action',
          title: 'Install an Integration',
        },
      ];
});

const { iteratively } = useIteratively();
type Link = {
  name: string;
  icon: string;
  dataTestid: string;
  title: string;
};
type LinkOrButtonProps = {
  'data-testid': string;
  type?: string;
  variant?: string;
  text?: string;
  leftIcon?: string[];
  size?: string;
  class?: string;
  to?: { name: string };
};
function getProps(link: Link): LinkOrButtonProps {
  let props: LinkOrButtonProps = {
    'data-testid': link.dataTestid,
  };
  if (hasPPV5.value) {
    props = {
      ...props,
      type: 'white',
      variant: 'outline',
      text: link.title,
      leftIcon: ['fak', link.icon],
      size: 'large',
    };
  } else {
    props = {
      ...props,
      class: 'action-button',
      to: { name: link.name },
    };
  }
  return props;
}
const hasTrackedUpgrade = ref(false);
function trackUpgrade(title?: string) {
  if (hasTrackedUpgrade.value) return;
  hasTrackedUpgrade.value = true;
  iteratively.userUpgradesToConnector({
    cta: title || '',
    event_site: EVENT_SITES.SELF_SERVE_FLOW,
  } as never);
}
function getEventHandlers(link: Link) {
  if (!hasPPV5.value) return {};
  return {
    click: () => {
      trackUpgrade(link.title);
      router.push({ name: link.name });
    },
  };
}

function trackLeavingWindow() {
  if (hasPPV5.value) trackUpgrade();
}
onMounted(() => {
  window.addEventListener('beforeunload', trackLeavingWindow);
});
onUnmounted(() => {
  window.removeEventListener('beforeunload', trackLeavingWindow);
});
onBeforeRouteLeave(() => {
  if (hasPPV5.value) trackUpgrade();
});

// TODO: Fix this with new sales edge roles
const migratedUsers = computed(() =>
  teamStore.authorizations
    .filter((auth) => auth.sales_edge_role === PARTNER_MANAGER)
    .map((auth) => auth.user),
);

const migrationCalloutTitle = computed(() => {
  if (migratedUsers.value.length === 1)
    return '1 user is now a Manager on Crossbeam for Sales';
  return `${migratedUsers.value.length} users are now Managers on Crossbeam for Sales`;
});

async function handleClose(closeButton = false) {
  if (hasPPV5.value && closeButton) {
    trackUpgrade('Close');
  } else if (hasPPV5.value) {
    trackUpgrade(
      hasOnboardingState.value ? 'Finish onboarding' : 'Continue to Crossbeam',
    );
  }
  if (hasOnboardingState.value) {
    await router.push({ name: 'home' });
    return;
  }
  await router.push({ name: 'team' });
}
</script>

<style lang="pcss" scoped>
.purchase-successful {
  @apply flex flex-1 flex-col items-center px-16 pb-48 min-h-screen;
  /* This comes directly from Figma 😁 */
  background: radial-gradient(
      57.15% 41.02% at 23.19% 83.74%,
      theme(colors.upsell.accent / 0.8) 0%,
      theme(colors.upsell.accent / 0) 100%
    ),
    radial-gradient(
      71.71% 50.59% at 87.36% -3.52%,
      theme(colors.info.accent / 0.8) 0%,
      theme(colors.info.accent / 0) 100%
    ),
    linear-gradient(
      180deg,
      theme(colors.info.accent) 0%,
      theme(colors.primary.text-button) 100%
    );
}

.welcome-to-the-team__content {
  @apply flex flex-col items-center h-full flex-1 md:mt-80;
}

.welcome-to-the-team__logo {
  @apply w-64 h-64 md:w-80 md:h-80 mb-40 mt-72;
}

.main-title {
  background: linear-gradient(
    91.59deg,
    theme(colors.info.accent) 0%,
    theme(colors.white) 100%
  );
  @apply text-xl md:text-xxxl bg-clip-text font-bold mb-16 text-center;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
}

.description {
  @apply text-m md:text-lg text-white text-center mb-40 opacity-75;
  max-width: 560px;
}

.action-button {
  @apply text-white rounded-16 py-16 flex items-center gap-8 pl-16 md:w-[330px] text-m border border-transparent border-r-0;
  background: linear-gradient(
    100.88deg,
    theme(colors.white / 0.2) 0%,
    theme(colors.white / 0) 100%
  );

  &:hover {
    @apply border-info-accent border-r;
  }
}

.button-grid {
  @apply grid md:grid-cols-2 gap-16 w-full;
}

.close-icon {
  @apply absolute top-16 right-16;
}

.purchase-successful--ppv5 {
  background: radial-gradient(
      70.48% 50% at 0% 100%,
      theme(colors.brand-teal / 0.8) 0%,
      theme(colors.brand-teal / 0) 100%
    ),
    radial-gradient(
      50.07% 71.24% at 100% 0%,
      theme(colors.secondary.accent / 0.8) 0%,
      theme(colors.secondary.accent / 0) 100%
    ),
    theme(colors.brand-navy);
  .welcome-to-the-team__logo {
    @apply mb-24;
  }
  .main-title {
    @apply mb-40;
  }
  .description {
    @apply max-w-full font-bold;
  }
  .button-grid {
    @apply grid md:grid-cols-2 gap-24 w-full;
  }
}
</style>
